import { useState, useEffect, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';

import EditIcon from 'app/components/DataGrid/EditIcon';
import TextFilter from 'app/components/DataGrid/TextFilter';
import SelectFilter from 'app/components/DataGrid/SelectFilter';
import AutocompleteFilter from 'app/components/DataGrid/AutocompleteFilter';
import DateFilter from 'app/components/DataGrid/DateFilter';
import NumberFilter from 'app/components/DataGrid/NumberFilter';

const useColumns = props => {
  const {
    // resource to pass to filter components for Redux connection
    resource,

    // filterModel to show normally hidden columns that have filters set
    filterModel,

    // arrays of colDef field names to control order, presence and visibility
    onlyColumns,
    omitColumns,

    // columnDefOverrides is the same as AG Grid's columnDefs prop,
    // with some extra features added by this component.
    // by default the columnDefs are built from the rowData.
    // any columnDef defined here will override the existing one
    // or create a new one if it wasn't created by the rowData.
    // so this can be used to add new columnDefs (like pinned actions, etc.)
    columnDefOverrides,

    // flag and callback for edit icon on each row
    showEdit,
    onEdit,

    // flag to show row checkboxes
    showSelect,

    // flag to toggle mobile styling
    isMobile,

    // function that renders cell (unless overridden)
    cellRenderer,

    // values from gridState hook
    gridStateColumnOrder,
    gridStateVisibleColumns,
    gridStatePinnedColumns
  } = props;

  const colOverrides = useRef(columnDefOverrides);
  useEffect(() => {
    colOverrides.current = columnDefOverrides;
  }, [columnDefOverrides])

  // keep track of rowData so we can recreate the columns without fetching it
  const rowDataRef = useRef(null);

  const onVisibleColumnsUpdateRef = useRef(null);
  const onVisibleColumnsUpdate = callback => {
    onVisibleColumnsUpdateRef.current = callback;
  }

  const [state, setState] = useState({
    columnDefs: [],
    lastUpdate: Date.now()
  });

  const setColumnDefs = columnDefs => {
    setState({ columnDefs, lastUpdate: Date.now() });
  }

  const setColumnOrder = columnOrder => {
    setColumnDefs(applyColumnOrder(state.columnDefs, columnOrder));
  }

  // support temporarily visible columns.
  // these columns should appear but not be saved to the grid state.
  // they should also appear immediately after the pinned columns.
  // this is used to show invisible columns that have a filter set,
  // without making them persistent (ex: ID columns added by a deeplink)
  const temporaryColumnsRef = useRef([]);

  // predefined column defs
  const checkboxColumnDef = {
    headerName: '',
    field: 'checkbox',
    actionIcon: true,
    width: 3 * 16, // 3rem
    suppressSizeToFit: true,
    pinned: 'left',
    // headerCheckboxSelection: true, // "select all" Not supported with infinite scroll
    checkboxSelection: true,
    cellStyle: { padding: '0.65rem 1.2rem' }
  }

  const editIconColumnDef = {
    headerName: '',
    field: 'edit',
    actionIcon: true,
    width: 3 * 16, // 3rem, defaults to 200px for some reason
    suppressSizeToFit: true,
    pinned: 'left',
    cellRenderer: ({ data }) => ((
      <EditIcon
        data={data}
        //disabled={props.quickEdit.active} // removed quickEdit prop
        onEdit={onEdit}
      />
    )),
    cellStyle: { padding: '0.5rem', textAlign: 'center' }
  }

  // columnDef filters for buildColumnDefs
  const initColumnDefs = rowData => {
    // use first row to build columnDefs
    let firstRow = rowData[0];
    return Object.keys(firstRow).map(key => ({
      field: key,
      resizable: true,
      sortable: true,
      visible: true
    }));
  }

  const applyColumnDefOverrides = columnDefs => {
    if (!colOverrides.current) {
      return columnDefs;
    }

    columnDefs = columnDefs.map(colDef => {
      let override = colOverrides.current.find(c => c.field === colDef.field);
      return override ? { ...colDef, ...override } : colDef;
    });

    // add any new columnDefs
    let existingFields = columnDefs.map(colDef => colDef.field);
    let newColDefs = colOverrides.current.filter(colDef => {
      return existingFields.indexOf(colDef.field) === -1;
    });

    // if any new columnDefs are action icons then set defaults.
    // custom action icons only need a field, cellRenderer
    // and the actionIcon flag
    for (let colDef of newColDefs) {
      if (colDef.actionIcon) {
        colDef.headerName ||= '';
        colDef.width ||= 3 * 16; // 3rem
        colDef.suppressSizeToFit = true;
        colDef.pinned = 'left';
        colDef.cellStyle ||= { padding: '0 0.5rem', textAlign: 'center' };
        colDef.cellStyle.cursor = 'pointer';
      }
    }

    return columnDefs.concat(newColDefs);
  }

  const applyPinnedColumns = columnDefs => {
    // prepend pinned columns
    let pinnedColumns = []

    if (showSelect) {
      // add select all checkbox
      pinnedColumns.push(checkboxColumnDef);
    }

    if (showEdit) {
      // add column for edit icon
      pinnedColumns.push(editIconColumnDef);
    }

    // pin and lock the columns
    for (let colDef of pinnedColumns) {
      colDef.pinned = 'left';
      colDef.lockPosition = 'left';
    }

    // get any pinned columns stored in the grid state
    if (gridStatePinnedColumns) {
      for (let field of gridStatePinnedColumns) {
        for (let i = 0, l = columnDefs.length; i < l; i++) {
          let colDef = columnDefs[i];
          if (colDef.field === field) {
            // pin (but don't lock) the column
            colDef.pinned = 'left';

            // add the colDef to the pinnedColumns array
            pinnedColumns.push(colDef);

            // remove the colDef from the columnDefs (will be re-added later)
            columnDefs.splice(i, 1);

            break;
          }
        }
      }
    }

    // prepend the pinned columns to the columnDefs
    return pinnedColumns.concat(columnDefs);
  }

  const applyMobile = columnDefs => {
    if (!isMobile) {
      return columnDefs;
    }

    columnDefs.forEach(colDef => {
      if (colDef.actionIcon && colDef.field !== 'checkbox' && colDef.field !== 'edit') {
        colDef.visible = false;
      }
    });

    return columnDefs;
  }

  const applyOnlyColumns = columnDefs => {
    // only render fields in onlyColumns array if set
    if (onlyColumns) {
      return columnDefs.filter(colDef => colDef.actionIcon || onlyColumns.indexOf(colDef.field) !== -1);
    }
    return columnDefs;
  }

  const applyOmitColumns = columnDefs => {
    // omit fields in omitColumns array if set
    if (omitColumns) {
      return columnDefs.filter(colDef => colDef.actionIcon || omitColumns.indexOf(colDef.field) === -1);
    }
    return columnDefs;
  }

  const applyDefaultHeaderNames = columnDefs => {
    for (let colDef of columnDefs) {
      if (colDef.headerName === undefined) {
        colDef.headerName = colDef.field.split('_')
          .map(word => {
            return word.charAt(0).toUpperCase() + word.toLowerCase().slice(1);
          })
          .join(' ');
      }
    }

    return columnDefs;
  }

  const applyFilters = columnDefs => {
    // setup filters
    for (let colDef of columnDefs) {
      // disable & skip if actionIcon or filter disabled
      if (colDef.actionIcon || colDef.filterDisabled) {
        colDef.floatingFilter = false;
        colDef.filter = false;
        continue;
      }

      colDef.floatingFilter = true;

      switch (colDef.inputType) {
        case 'number':
          colDef.filter = 'agNumberColumnFilter';
          break;
        case 'date':
          colDef.filter = 'agDateColumnFilter';
          break;
        default:
          colDef.filter = 'agTextColumnFilter';
      }

      // set filter component based on inputType
      if (!colDef.floatingFilterComponent) {
        colDef.suppressMenu = true;

        switch (colDef.inputType) {
          case 'autocomplete':
            colDef.floatingFilterComponent = AutocompleteFilter;
            break;

          case 'select':
            colDef.floatingFilterComponent = SelectFilter;
            break;

          case 'date':
            colDef.floatingFilterComponent = DateFilter;
            break;

          case 'number':
            colDef.floatingFilterComponent = NumberFilter;
            break;

          default:
            colDef.floatingFilterComponent = TextFilter;
        }
      }

      colDef.floatingFilterComponentParams = {
        resource,
        suppressFilterButton: true
      }
    }

    return columnDefs;
  }

  const applyVisibleColumns = columnDefs => {
    for (let colDef of columnDefs) {
      // only show fields in grid state if set, else show all
      if (gridStateVisibleColumns) {
        colDef.visible = colDef.actionIcon ||
          gridStateVisibleColumns.indexOf(colDef.field) !== -1;

      } else if (colDef.visible !== false) {
        colDef.visible = true;
      }

      // show all temporary columns
      // (columns that won't be saved to the grid state)
      if (temporaryColumnsRef.current.indexOf(colDef.field) !== -1) {
        colDef.visible = true;
      }
    }

    return columnDefs
  }

  const applyMinWidth = (columnDefs, rowData) => {
    // set min width for each column.
    // set min width according to longest value, unless too long.
    // only consider first 10 non-empty rows for efficiency
    for (let colDef of columnDefs) {
      // skip column if minWidth, width or suppressSizeToFit are set
      if (colDef.minWidth || colDef.width || colDef.suppressSizeToFit || colDef.autoHeight) {
        continue;
      }

      // if column is a thumbnail then set to fixed thumbnail width
      if (colDef.thumbnail) {
        colDef.width = 8 * 16; // 8rem
        colDef.minWidth = 8 * 16; // 8rem
        continue;
      }

      // set to longest of first 10 non-empty rows.
      // figure 16px * string length, unless over max
      let longestLength = null;
      let rowsDeep = 0;
      let maxRowsDeep = 10;
      for (let i = 0, l = rowData.length; i < l; i++) {
        let row = rowData[i];
        let value = row[colDef.field];

        if (colDef.valueFormatter) {
          value = colDef.valueFormatter(value);

          // if formatted value is JSX then render to HTML and get innerText
          const isJSX = v => typeof v === 'object' && v.$$typeof;
          if (isJSX(value)) {
            let html = ReactDOMServer.renderToStaticMarkup(value);
            value = new window.DOMParser().parseFromString(html, 'text/html').firstChild.innerText;
          }
        }

        if (value) {
          rowsDeep++;

          let length = value.toString().length;
          if (length > longestLength) {
            longestLength = length;
          }

          if (rowsDeep >= maxRowsDeep) {
            break;
          }
        }
      }

      // use the label width if greater than the longest length.
      // offset 5em for filter icon.
      let labelLength = colDef.headerName.length + 5;
      if (labelLength > longestLength) {
        longestLength = labelLength;
      }

      // take the longest width or the max width if too long
      let maxWidth = 300;
      let colWidth = longestLength * 13; // 1em = 13px
      if (colDef.excludeFromMaxWidth !== true) {
        if (colWidth > maxWidth) {
          colWidth = maxWidth;
        }
      }
      // set as initial width for the column
      colDef.width = colWidth;

      // set as initial minWidth for the column for smaller screens (i.e. mobile in landscape)
      colDef.minWidth = colWidth / 2;
    }

    return columnDefs;
  }

  const applyCellRenderer = columnDefs => {
    // set cell renderers on column defs
    for (let colDef of columnDefs) {
      // if cellRenderer is defined then leave as is,
      // unless quick edit is enabled and active
      //let skipCellRenderer = colDef.cellRenderer &&
      //  (!colDef.quickEdit || !props.quickEdit.active);
      // note - removed quickEdit prop, revisit if needed
      let skipCellRenderer = colDef.cellRenderer && !colDef.quickEdit;

      if (skipCellRenderer) {
        continue;
      }

      colDef.cellRenderer = cellRenderer;
    }

    return columnDefs;
  }

  const applyColumnOrder = (columnDefs, columnOrder = []) => {
    // order columns first by order of temporaryColumns array,
    // then by the columnOrder array.
    // remaining columns will be appended in existing order

    // helper function for sorting.
    // get field index from array, return a large number if not found
    const getFieldIndex = (array, field) => {
      let index = array.indexOf(field);
      return index === -1 ? window.Number.MAX_SAFE_INTEGER : index;
    }

    // sort the column defs
    columnDefs.sort((a, b) => {
      // first sort by temporaryColumns array
      let indexA = getFieldIndex(temporaryColumnsRef.current, a.field);
      let indexB = getFieldIndex(temporaryColumnsRef.current, b.field);

      // the index will be a high number if the field isn't found
      if (indexA !== indexB) {
        return indexA - indexB;
      }

      // sort next by columnOrder array
      indexA = getFieldIndex(columnOrder, a.field);
      indexB = getFieldIndex(columnOrder, b.field);

      return indexA - indexB;
    });

    return columnDefs;
  }

  const buildColumnDefs = rowData => {
    if (!rowData.length) {
      return [];
    }

    // track rowData so that we can rebuild columns without fetching it
    rowDataRef.current = rowData;

    // build temporyColumns array from filterModel fields not found in gridStateVisibleColumns
    temporaryColumnsRef.current = Object.keys(filterModel || {}).filter(field => {
      return gridStateVisibleColumns.indexOf(field) === -1;
    });

    let columnDefs = initColumnDefs(rowData);

    columnDefs = applyColumnDefOverrides(columnDefs);
    columnDefs = applyPinnedColumns(columnDefs);
    columnDefs = applyOnlyColumns(columnDefs);
    columnDefs = applyOmitColumns(columnDefs);
    columnDefs = applyDefaultHeaderNames(columnDefs);
    columnDefs = applyFilters(columnDefs);
    columnDefs = applyVisibleColumns(columnDefs);
    columnDefs = applyMobile(columnDefs);
    columnDefs = applyCellRenderer(columnDefs)
    columnDefs = applyColumnOrder(columnDefs, gridStateColumnOrder);
    columnDefs = applyMinWidth(columnDefs, rowData);

    setColumnDefs(columnDefs);
  }

  const rebuildColumnDefs = () => buildColumnDefs(rowDataRef.current);

  const toggleColumnDefsMobileStyling = () => {
    // currently action icons are hidden on mobile, except the checkbox
    let colDefs = state.columnDefs.map(colDef => {
      if (colDef.actionIcon && colDef.field !== 'checkbox' && colDef.field !== 'edit') {
        colDef.visible = !isMobile;
      }
      return colDef;
    });

    setColumnDefs(colDefs);
  }
  useEffect(toggleColumnDefsMobileStyling, [isMobile]);

  const toggleColumnDefs = visibleFields => {
    let colDefs = state.columnDefs.map(colDef => {
      colDef.visible = visibleFields.indexOf(colDef.field) !== -1;
      return colDef;
    });

    setColumnDefs(colDefs);

    // update visibleColumns server-side
    onVisibleColumnsUpdateRef.current(visibleFields);
  }

  const setColumnDefVisible = (field, visible) => {
    let colDefs = state.columnDefs.map(colDef => {
      if (colDef.field === field) {
        colDef.visible = visible;
      }
      return colDef;
    });

    // update visibleColumns server-side.
    // map colDef fields & remove the updated field from the list
    let params = state.columnDefs
      .filter(colDef => colDef.visible && colDef.field !== field)
      .map(colDef => colDef.field);

    // re-add the updated field if it's been made visible
    if (visible) {
      params.push(field);
    }

    onVisibleColumnsUpdateRef.current(params);

    setColumnDefs(colDefs);
  }

  const toggleColumnDefVisible = field => {
    let colDef = state.columnDefs.find(colDef => colDef.field === field);
    setColumnDefVisible(field, !colDef.visible);
  }

  const setColumnDefPinned = (field, pinned) => {
    // update the state so that the columnDef is pinned appropriately
    setColumnDefs(state.columnDefs.map(colDef => {
      if (colDef.field === field) {
        if (pinned) {
          colDef.pinned = 'left';
        } else {
          colDef.pinned = null;
        }
      }

      return colDef;
    }));
  }

  const getVisibleColumnDefs = quickEditActive => {
    // if quick edit mode is active, then only show editable fields.
    // otherwise show visible fields
    if (quickEditActive) {
      return state.columnDefs.filter(colDef => colDef.quickEdit);
    }

    return state.columnDefs.filter(colDef => colDef.visible);
  }

  const toggleQuickEditActive = active => {
    // toggle quickEditActive on each column.
    // for some reason quickEdit.active isn't updating in the Cell component,
    // but the columnDefs are.
    // using the flag on each colDef instead of quickEdit.active for now
    let colDefs = state.columnDefs.map(colDef => {
      // if quick edit is enabled for column
      if (colDef.quickEdit) {
        colDef.quickEditActive = active;
      }
      return colDef;
    });

    colDefs = applyVisibleColumns(colDefs);
    colDefs = applyCellRenderer(colDefs);

    setColumnDefs(colDefs);
  }

  return {
    columnDefs: state.columnDefs,
    setColumnDefs,
    buildColumnDefs,
    rebuildColumnDefs,
    setColumnOrder,
    setColumnDefVisible,
    toggleColumnDefVisible,
    toggleColumnDefs,
    setColumnDefPinned,
    getVisibleColumnDefs,
    onVisibleColumnsUpdate,
    lastUpdate: state.lastUpdate,
    toggleQuickEditActive
  }
}

export default useColumns;
